import {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { CalendarIcon } from 'lucide-react';

import { Calendar } from '../shadcn/calendar';
import { TCommonInputProps } from './useInputLogic';
import { InputText } from './InputText';
import {
  formatDateInputValue,
  getDobOldestAllowedDate,
  getDobStartMonth,
  getMonthStart,
  getTodayInputDate,
  parseIsoDateInput,
} from './input-date.lib';

export type TInputDateProps = TCommonInputProps & {
  calendarTestId?: string;
  onChange?: (value: string, event?: ChangeEvent<HTMLInputElement>) => void;
};

type TCalendarPosition = {
  left: number;
  top: number;
};

const CALENDAR_SIDE_OFFSET_PX = 6;

export const InputDate = forwardRef<HTMLInputElement, TInputDateProps>(
  function InputDate(
    {
      calendarTestId,
      disabled,
      onChange,
      onFocus,
      readOnly,
      value = '',
      ...props
    }: TInputDateProps,
    ref,
  ) {
    const inputRef = useRef<HTMLInputElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const calendarRef = useRef<HTMLDivElement>(null);
    const isIgnoringNextInputFocusRef = useRef(false);
    const buttonId = useId();
    const [isOpen, setIsOpen] = useState(false);
    const [calendarPosition, setCalendarPosition] =
      useState<TCalendarPosition | null>(null);
    const todayDate = useMemo(() => getTodayInputDate(), []);
    const oldestAllowedDate = useMemo(
      () => getDobOldestAllowedDate(todayDate),
      [todayDate],
    );
    const selectedDate = useMemo(
      () => parseIsoDateInput(value.trim()),
      [value],
    );
    const [visibleMonth, setVisibleMonth] = useState<Date>(() =>
      getMonthStart(selectedDate ?? todayDate),
    );

    useImperativeHandle(ref, () => inputRef.current!);

    const updateCalendarPosition = useCallback((): void => {
      const inputElement = inputRef.current;
      if (!inputElement) {
        return;
      }

      const inputRect = inputElement.getBoundingClientRect();
      setCalendarPosition({
        left: inputRect.left,
        top: inputRect.bottom + CALENDAR_SIDE_OFFSET_PX,
      });
    }, []);

    useEffect(() => {
      if (!isOpen) {
        return;
      }

      updateCalendarPosition();

      const handleWindowChange = (): void => {
        updateCalendarPosition();
      };

      const handlePointerDown = (event: PointerEvent): void => {
        const target = event.target;
        if (!(target instanceof Node)) {
          return;
        }

        if (
          rootRef.current?.contains(target) ||
          calendarRef.current?.contains(target)
        ) {
          return;
        }

        setIsOpen(false);
      };

      const handleEscape = (event: KeyboardEvent): void => {
        if (event.key !== 'Escape') {
          return;
        }

        setIsOpen(false);
        inputRef.current?.focus();
      };

      window.addEventListener('resize', handleWindowChange);
      window.addEventListener('scroll', handleWindowChange, true);
      document.addEventListener('pointerdown', handlePointerDown);
      document.addEventListener('keydown', handleEscape);

      return () => {
        window.removeEventListener('resize', handleWindowChange);
        window.removeEventListener('scroll', handleWindowChange, true);
        document.removeEventListener('pointerdown', handlePointerDown);
        document.removeEventListener('keydown', handleEscape);
      };
    }, [isOpen, updateCalendarPosition]);

    const handleOpenChange = (nextValue: boolean): void => {
      if ((disabled || readOnly) && nextValue) {
        return;
      }

      if (nextValue) {
        setVisibleMonth(getMonthStart(selectedDate ?? todayDate));
        updateCalendarPosition();
      }

      setIsOpen(nextValue);
    };

    const handleCalendarSelect = (nextDate: Date | undefined): void => {
      if (!nextDate) {
        return;
      }

      onChange?.(formatDateInputValue(nextDate));
      setIsOpen(false);
      isIgnoringNextInputFocusRef.current = true;
      inputRef.current?.focus();
    };

    const handleInputFocus = (event: FocusEvent<HTMLInputElement>): void => {
      onFocus?.(event);

      if (isIgnoringNextInputFocusRef.current) {
        isIgnoringNextInputFocusRef.current = false;
        return;
      }

      handleOpenChange(true);
    };

    const handleTriggerClick = (): void => {
      handleOpenChange(!isOpen);
    };

    const calendarDropdown =
      isOpen && calendarPosition && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="fixed z-50"
              style={{ left: calendarPosition.left, top: calendarPosition.top }}
            >
              <div
                className="w-auto rounded-md border border-white/10 bg-[#0a0a0d] p-0 text-white shadow-md outline-none"
                data-testid={calendarTestId}
                id={buttonId}
                ref={calendarRef}
                role="dialog"
              >
                <Calendar
                  captionLayout="dropdown"
                  className="rounded-2xl"
                  classNames={{
                    dropdown:
                      'h-8 rounded-md border-0 bg-[#141418] px-2 pr-8 text-sm text-white outline-none',
                    dropdown_root:
                      'relative rounded-md border border-white/10 bg-[#141418] text-white shadow-none',
                  }}
                  defaultMonth={selectedDate ?? todayDate}
                  disabled={{ after: todayDate, before: oldestAllowedDate }}
                  endMonth={getMonthStart(todayDate)}
                  fixedWeeks
                  mode="single"
                  month={visibleMonth}
                  navLayout="around"
                  onMonthChange={setVisibleMonth}
                  onSelect={handleCalendarSelect}
                  selected={selectedDate}
                  showOutsideDays
                  startMonth={getDobStartMonth(todayDate)}
                />
              </div>
            </div>,
            document.body,
          )
        : null;

    return (
      <div className="relative" ref={rootRef}>
        <InputText
          {...props}
          disabled={disabled}
          onChange={onChange}
          onFocus={handleInputFocus}
          readOnly={readOnly}
          ref={inputRef}
          value={value}
        >
          <button
            aria-controls={buttonId}
            aria-expanded={isOpen}
            aria-haspopup="dialog"
            aria-label="Choose date from calendar"
            className="inline-flex h-6 w-6 items-center justify-center rounded-md border-0 bg-transparent p-0 text-white/70 transition-colors hover:bg-white/5 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-40"
            disabled={disabled}
            onClick={handleTriggerClick}
            type="button"
          >
            <CalendarIcon className="h-4 w-4" />
          </button>
        </InputText>

        {calendarDropdown}
      </div>
    );
  },
);
